import {useContext, useMemo, useState} from 'react';

import {useTranslation} from 'react-i18next';

import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import {Box, Typography} from '@mui/material';

import {WorkforceProfileAlignmentFilterButton} from './WorkforceProfileAlignmentFilterButton';
import {WorkforceProfileAlignmentItemsGrid} from './WorkforceProfileAlignmentItemsGrid';
import {WorkforceProfileAlignmentSectionLabel} from './WorkforceProfileAlignmentSectionLabel';
import {EmpionPaper} from '../../../../../components/EmpionPaper/EmpionPaper';
import {LanguageContext} from '../../../../../contexts/LanguageContext/LanguageContext';
import {alignmentStatus, orangeColors} from '../../../../../layouts/theme/designSystem';
import {WorkforceProfileCompetencyRow} from '../../types/WorkforceProfileTypes';

type AlignmentFilter = 'all' | 'met' | 'gap';

export type AlignmentItemKind =
  | 'coreCompetencies'
  | 'hardSkills'
  | 'tools'
  | 'domainKnowledge'
  | 'softSkills'
  | 'responsibilities';

export interface Props {
  title: string;
  rows: WorkforceProfileCompetencyRow[];
  itemKind: AlignmentItemKind;
  sx?: {mb?: number};
  newlyMetKeys?: Set<string>;
  newlyMetNote?: string;
}

export const WorkforceProfileAlignmentItemsOverview = ({
  title,
  rows,
  itemKind,
  sx,
  newlyMetKeys,
  newlyMetNote,
}: Props) => {
  const {t} = useTranslation('translation', {keyPrefix: 'workforceProfilesPage.detail.alignmentOverview'});
  const currentLanguage = useContext(LanguageContext);
  const [filter, setFilter] = useState<AlignmentFilter>('all');

  const {metRows, gapRows} = useMemo(
    () => ({
      metRows: rows.filter((row) => row.met),
      gapRows: rows.filter((row) => !row.met),
    }),
    [rows]
  );

  if (rows.length === 0) {
    return null;
  }

  const showMetSection = filter !== 'gap' && metRows.length > 0;
  const showGapSection = filter !== 'met' && gapRows.length > 0;
  const metCount = metRows.length;
  const gapCount = gapRows.length;

  return (
    <EmpionPaper
      sx={{
        border: `1px solid ${orangeColors[3]}`,
        borderRadius: '14px',
        p: 2.5,
        mb: sx?.mb ?? 2.5,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1.25,
          mb: 0.5,
        }}
      >
        <Box sx={{display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap'}}>
          <Typography sx={{fontSize: 15, fontWeight: 500, color: 'text.primary'}}>
            {title}
          </Typography>
          {metCount > 0 && (
            <Box
              component='span'
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.625,
                fontSize: 12,
                fontWeight: 500,
                px: '10px',
                py: '3px',
                borderRadius: '20px',
                bgcolor: alignmentStatus.metChipBg,
                color: alignmentStatus.metChipFg,
              }}
            >
              <CheckIcon sx={{fontSize: 13}} aria-hidden />
              {t('summaryMet', {count: metCount})}
            </Box>
          )}
          {gapCount > 0 && (
            <Box
              component='span'
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.625,
                fontSize: 12,
                fontWeight: 500,
                px: '10px',
                py: '3px',
                borderRadius: '20px',
                bgcolor: alignmentStatus.gapChipBg,
                color: alignmentStatus.gapChipFg,
              }}
            >
              <CloseIcon sx={{fontSize: 13}} aria-hidden />
              {t('summaryGaps', {count: gapCount})}
            </Box>
          )}
        </Box>
        <Box sx={{display: 'flex', gap: 0.75}}>
          <WorkforceProfileAlignmentFilterButton
            label={t('filterAll')}
            active={filter === 'all'}
            onClick={() => setFilter('all')}
          />
          <WorkforceProfileAlignmentFilterButton
            label={t('filterMet')}
            active={filter === 'met'}
            onClick={() => setFilter('met')}
          />
          <WorkforceProfileAlignmentFilterButton
            label={t('filterGaps')}
            active={filter === 'gap'}
            onClick={() => setFilter('gap')}
          />
        </Box>
      </Box>

      {showMetSection && (
        <>
          <WorkforceProfileAlignmentSectionLabel
            met
            isFirst
            title={t('sectionMet')}
            subtitle={t(`metCountOfTotal.${itemKind}`, {met: metCount, total: rows.length})}
          />
          <WorkforceProfileAlignmentItemsGrid
            rows={metRows}
            met
            language={currentLanguage}
            newlyMetKeys={newlyMetKeys}
            newlyMetNote={newlyMetNote}
          />
        </>
      )}

      {showGapSection && (
        <>
          <WorkforceProfileAlignmentSectionLabel
            met={false}
            isFirst={!showMetSection}
            title={t('sectionNotMet')}
            subtitle={t('gapsToAddress', {count: gapCount})}
          />
          <WorkforceProfileAlignmentItemsGrid
            rows={gapRows}
            met={false}
            language={currentLanguage}
            newlyMetKeys={newlyMetKeys}
          />
        </>
      )}
    </EmpionPaper>
  );
};
